import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import * as api from "./electricalApi";
import "./ElectricalCapture.css";

/*
 * react-select ships its own default border color - without an override
 * here it reads visibly different from every plain <input> on this form
 * (.new-audit input, border: 1px solid var(--rule)), even though both sit
 * in the same list of fields. Matched to the same color/radius, and to the
 * same focus treatment (an outline, not react-select's own blue glow).
 */
const selectBoxStyles = {
  control: (provided, state) => ({
    ...provided,
    maxHeight: "80px",
    overflowY: "auto",
    display: "flex",
    flexWrap: "wrap",
    borderColor: "var(--rule)",
    borderRadius: 4,
    boxShadow: "none",
    outline: state.isFocused ? "2px solid var(--teal-lt)" : "none",
    outlineOffset: -1,
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: "80px",
    overflowY: "auto",
  }),
};

/**
 * Start an Electrical audit. Modeled directly on Csa/CsaNewAudit.js.
 *
 * Client and site come first, before any checkpoint is answered - the report
 * is for a site, and every later screen names it.
 *
 * A client typed here that doesn't exist is created - blocking the first
 * audit for a new client on an admin would stop the module being usable on
 * day one. Existing names are matched case-insensitively, so nobody ends up
 * with two spellings of the same client splitting one site's history.
 *
 * Nominating a reviewer is optional and never a gate - the report can be
 * issued from "submitted" with nobody nominated; it only determines who sees
 * the audit in their review list.
 */
export default function ElectricalNewAudit({ onCancel }) {
  const navigate = useNavigate();
  const back = onCancel || (() => navigate("/electrical-audits"));

  const [orgs, setOrgs] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [auditors, setAuditors] = useState([]);
  const [auditorId, setAuditorId] = useState("");
  const [clash, setClash] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .listOrganizations()
      .then((r) => setOrgs(Array.isArray(r) ? r : []))
      .catch((e) => setError(e.message));
  }, []);

  /*
   * Never blocks the form. If the lookup fails, the audit is still bookable
   * without an assignee - whoever creates it is the auditor by default.
   */
  useEffect(() => {
    api
      .listPeople("Auditor")
      // The shared axios response interceptor unwraps any non-/api/csa/
      // response down to its one array-valued field, so this arrives as a
      // bare array, not {role, people}. Accept either shape.
      .then((r) => setAuditors(Array.isArray(r) ? r : (r && r.people) || []))
      .catch(() => setAuditors([]));
  }, []);

  /*
   * Ask before saving, not after. A clash discovered once the audit exists is
   * a double-booking; this is a warning while it can still be avoided.
   * Debounced, because it fires on every date keystroke.
   */
  useEffect(() => {
    if (!auditorId || !start || !end || end < start) { setClash(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api
        .scheduleConflicts(auditorId, start, end)
        .then((r) => {
          if (!live) return;
          // Same unwrapping hazard as listPeople: {conflicts, summary} can
          // arrive as a bare conflicts array, with `summary` already gone.
          const conflicts = Array.isArray(r) ? r : (r && r.conflicts) || [];
          setClash(
            conflicts.length
              ? { conflicts, summary: (r && r.summary) || `${conflicts.length} schedule conflict${conflicts.length === 1 ? "" : "s"}` }
              : null
          );
        })
        .catch(() => live && setClash(null));
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [auditorId, start, end]);

  useEffect(() => {
    api
      .listPeople("Reviewer")
      // Same unwrapping hazard as the Auditor effect above.
      .then((r) => setReviewers(Array.isArray(r) ? r : (r && r.people) || []))
      .catch(() => setReviewers([]));
  }, []);

  // react-select options for the auditor/reviewer pickers - kept as single
  // selects (no isMulti) so they look like the area/category fields above
  // rather than native <select> elements.
  const auditorOptions = [
    { value: "", label: "Me" },
    ...auditors.map((p) => ({ value: String(p.id), label: p.name })),
  ];
  const reviewerOptions = [
    { value: "", label: "Nobody — this audit can still be issued" },
    ...reviewers.map((p) => ({ value: String(p.id), label: p.name })),
  ];

  const known = orgs.find(
    (o) => o.org_name.toLowerCase() === orgName.trim().toLowerCase()
  );
  const isNewClient = orgName.trim() && !known;
  // Not gated on `known` - when the client itself is also new there is no
  // existing site list to check against, so any typed site name is new too.
  // Requiring `known` here (as this used to) meant a brand new client +
  // site together never ran the site validation at all.
  const siteIsNew =
    !!siteName.trim() &&
    !(known && known.sites.some((s) => s.site_name.toLowerCase() === siteName.trim().toLowerCase()));

  /*
   * Same character set and length limit the old Electrical report modal
   * enforces when a client/site name is new rather than picked from the
   * existing list (ReportModal.js's handleOrganizationSelection/
   * handleSiteSelection) - an existing name was already validated when it
   * was first created, so only a genuinely new one is checked here. Site
   * allows a comma instead of a period - the one deliberate difference
   * between the two patterns in the original.
   */
  const ORG_NAME_RE = /^[a-zA-Z0-9 ._-]+$/;
  const SITE_NAME_RE = /^[a-zA-Z0-9 ,_-]+$/;
  const MAX_NAME_LEN = 30;

  const orgNameInvalid = !!isNewClient && !ORG_NAME_RE.test(orgName.trim());
  const orgNameTooLong = !!isNewClient && orgName.trim().length > MAX_NAME_LEN;
  const siteNameInvalid = !!siteIsNew && !SITE_NAME_RE.test(siteName.trim());
  const siteNameTooLong = !!siteIsNew && siteName.trim().length > MAX_NAME_LEN;

  const ready =
    orgName.trim() && siteName.trim() && start && end && !busy &&
    !orgNameInvalid && !orgNameTooLong && !siteNameInvalid && !siteNameTooLong;
  const datesWrong = start && end && end < start;

  const blockedReason = (() => {
    if (orgNameTooLong) return `Organization name cannot exceed ${MAX_NAME_LEN} characters. You have typed ${orgName.trim().length} characters.`;
    if (orgNameInvalid) return "Invalid organization name. Only spaces, periods ( . ), hyphens ( - ), underscores ( _ ) and alphanumeric characters are allowed.";
    if (siteNameTooLong) return `Site name cannot exceed ${MAX_NAME_LEN} characters. You have typed ${siteName.trim().length} characters.`;
    if (siteNameInvalid) return "Invalid site name. Only commas ( , ), hyphens ( - ), underscores ( _ ) and alphanumeric characters are allowed.";
    if (datesWrong) return "The end date is before the start date.";
    if (!orgName.trim() || !siteName.trim() || !start || !end) return "Please complete all fields before proceeding.";
    return "";
  })();

  const create = async () => {
    if (!ready || datesWrong) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.createAudit({
        organization: orgName.trim(),
        site_name: siteName.trim(),
        start_date: start,
        end_date: end,
        assigned_reviewer_id: reviewerId ? Number(reviewerId) : undefined,
        prepared_by: auditorId ? Number(auditorId) : undefined,
      });

      if(r){
        console.log(r);
        navigate(`/electrical-audit/${r.audit_id}`);
      }

      // navigate(`/electrical-audit/${r.audit_id}`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  // console.log('==');
  // console.log('reviewers: ',reviewers);
  // console.log('auditors: ',auditors);
  return (
    <div className="elec-capture elec-screen">
      <div className="cap-writeup" style={{ maxWidth: 560, paddingTop: 32 }}>
        <div className="wu-head">
          <div>
            <h2>New electrical audit</h2>
            <p>You can stop and come back at any point - everything saves as you go.</p>
          </div>
        </div>

        {error ? <p className="wu-error">{error}</p> : null}

        <div className="new-audit">
          <label htmlFor="ena-org">Client</label>
          <CreatableSelect
            inputId="ena-org"
            className="elec-select"
            placeholder="Start typing, or enter a new client"
            formatCreateLabel={(input) => `Add "${input}" as a new client`}
            options={orgs.map((o) => ({ value: o.org_name, label: o.org_name }))}
            value={orgName ? { value: orgName, label: orgName } : null}
            onChange={(opt) => {
              // A site belongs to one organization - carrying it over to
              // whichever org is picked next (same name, different client)
              // would silently misfile the audit under the wrong site, same
              // as the old Electrical report modal clearing the site on
              // every org change (handleOrganizationSelection).
              setOrgName(opt ? opt.value : "");
              setSiteName("");
            }}
            isClearable
            styles={selectBoxStyles}
          />
          {orgNameTooLong ? (
            <p className="na-hint bad">
              Organization name cannot exceed {MAX_NAME_LEN} characters. You have typed{" "}
              {orgName.trim().length} characters.
            </p>
          ) : orgNameInvalid ? (
            <p className="na-hint bad">
              Invalid organization name. Only spaces, periods ( . ), hyphens ( - ), underscores
              ( _ ) and alphanumeric characters are allowed.
            </p>
          ) : isNewClient ? (
            <p className="na-hint new">“{orgName.trim()}” is new — it will be added to the client list.</p>
          ) : null}

          <label htmlFor="ena-site">Site</label>
          <CreatableSelect
            inputId="ena-site"
            className="elec-select"
            placeholder={known ? "Pick a site, or enter a new one" : "Site name"}
            formatCreateLabel={(input) => `Add "${input}" as a new site`}
            options={(known ? known.sites : []).map((s) => ({ value: s.site_name, label: s.site_name }))}
            value={siteName ? { value: siteName, label: siteName } : null}
            onChange={(opt) => setSiteName(opt ? opt.value : "")}
            isClearable
            styles={selectBoxStyles}
          />
          {siteNameTooLong ? (
            <p className="na-hint bad">
              Site name cannot exceed {MAX_NAME_LEN} characters. You have typed{" "}
              {siteName.trim().length} characters.
            </p>
          ) : siteNameInvalid ? (
            <p className="na-hint bad">
              Invalid site name. Only commas ( , ), hyphens ( - ), underscores ( _ ) and
              alphanumeric characters are allowed.
            </p>
          ) : siteIsNew ? (
            <p className="na-hint new">
              “{siteName.trim()}” is a new site for {known ? known.org_name : orgName.trim()}.
            </p>
          ) : null}

          <div className="na-dates">
            <div>
              <label htmlFor="ena-start">Start Date</label>
              <input
                id="ena-start"
                type="date"
                value={start}
                max={end || undefined}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="ena-end">End Date</label>
              <input
                id="ena-end"
                type="date"
                value={end}
                min={start || undefined}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          {datesWrong ? <p className="na-hint bad">The end date is before the start date.</p> : null}

          <label htmlFor="ena-auditor">
            Who it is for <span className="na-optional">— defaults to you</span>
          </label>
          <Select
            inputId="ena-auditor"
            className="elec-select"
            options={auditorOptions}
            value={auditorOptions.find((o) => o.value === auditorId) || auditorOptions[0]}
            onChange={(opt) => setAuditorId(opt ? opt.value : "")}
            styles={selectBoxStyles}
          />

          {clash ? (
            <div className="na-clash">
              <strong>{clash.summary}</strong>
              <ul>
                {clash.conflicts.map((c) => (
                  <li key={c.audit_id}>
                    {c.organization} · {c.site} — {c.start_date} to {c.end_date}
                    {c.booked_by ? (
                      <span className="na-owner">booked by {c.booked_by}</span>
                    ) : c.owned_by ? (
                      <span className="na-owner">audit owned by {c.owned_by}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p>
                You can still go ahead. Assigning them here takes them off those
                audits, which are then left with no auditor until their own
                project manager acts.
              </p>
            </div>
          ) : null}

          <label htmlFor="ena-reviewer">
            Reviewer <span className="na-optional">— optional</span>
          </label>
          <Select
            inputId="ena-reviewer"
            className="elec-select"
            options={reviewerOptions}
            value={reviewerOptions.find((o) => o.value === reviewerId) || reviewerOptions[0]}
            onChange={(opt) => setReviewerId(opt ? opt.value : "")}
            styles={selectBoxStyles}
          />
          {reviewerId ? (
            <p className="na-hint">
              This audit appears in their list once you submit it. They can sign
              it off or send it back — they cannot edit it.
            </p>
          ) : reviewers.length === 0 ? (
            <p className="na-hint">
              Nobody else holds the Reviewer role yet, so there is nobody to
              nominate. An Admin grants it.
            </p>
          ) : null}

          <div className="scope-actions">
            <button type="button" className="small-btn" onClick={back}>
              Cancel
            </button>
            <button
              type="button"
              className="cap-btn"
              disabled={!ready || !!datesWrong}
              title={!ready || datesWrong ? blockedReason : ""}
              onClick={create}
            >
              {busy ? "Creating…" : "Start the audit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
